import type { Timestamp } from 'firebase/firestore';
import type { AccountDoc, DealTable, DealTableSources } from '@/lib/types';

export type StageKey = 'discovery' | 'evaluation' | 'negotiation' | 'closing';
export type YnValue = 'yes' | 'partial' | 'no';
export type ChampionStrength = 'strong' | 'medium' | 'weak';
export type Confidence = 'unknown' | 'tentative' | 'confirmed';

export interface PipelineOwner {
  name: string;
  initials: string;
  color: string;
  /** Firebase UID of the analyst when the owner is the signed-in user. SF owners have no platform account, so this is null for them. */
  uid?: string | null;
  /** Salesforce User Id (`Opportunity.OwnerId`). Null when the account has no cohort opportunity / owner. */
  sfUserId?: string | null;
  /** Salesforce User email. Used for "My deals" matching against the caller's Firebase email. */
  email?: string | null;
}

/** Slim shape of `salesforceUsers/{sfUserId}` snapshot read by the UI. */
export interface SfUserSnapshot {
  name: string | null;
  email: string | null;
  isActive: boolean;
}

export interface PipelineEconomicBuyer {
  value: string | null;
  confidence: Confidence;
  note?: string | null;
}

export interface PipelineChampion {
  name: string | null;
  role: string | null;
  strength: ChampionStrength;
}

export interface PipelineLaunchDate {
  value: string | null;
  status: 'agreed' | 'tentative' | 'no';
}

export interface PipelineBudget {
  value: YnValue;
  note?: string | null;
}

export interface PipelineAccount {
  id: string;
  name: string;
  logo: string;
  logoColor: string;
  stage: StageKey;
  momentum: number;
  strength: number;
  arr: number;
  owner: PipelineOwner;
  economicBuyer: PipelineEconomicBuyer;
  ebSignedOff: YnValue;
  useCase: string | null;
  launchDateAgreed: PipelineLaunchDate;
  budgetApproved: PipelineBudget;
  blockers: string[];
  champion: PipelineChampion;
  legalContact: { name: string | null };
  msaTerms: 'not-sent' | 'in-review' | 'redlined' | 'signed';
  procurementContact: { name: string | null };
  vendorReg: 'not-started' | 'in-progress' | 'complete';
  nextSteps: string[];
  redFlags: string[];
  calls: number;
  /** Number of Salesforce emails included in the latest analysis. */
  emails: number;
  /**
   * Per-field source provenance from the latest synthesis. Undefined for
   * accounts whose latest analysis predates PROMPT_VERSION='2026-05-11.2',
   * in which case the UI renders no chips.
   */
  tableSources?: DealTableSources;
  lastActivity: Date | null;
  updatedAt: Date | null;
  hasAnalysis: boolean;
}

// ---------------------------------------------------------------------------
// Display metadata mirrors PIPELINE_HELPERS from the handoff data.js so the
// ported components can render with identical colors/labels.
// ---------------------------------------------------------------------------
export const stageMeta: Record<
  StageKey,
  { label: string; color: string; bg: string; dot: string }
> = {
  discovery: { label: 'Discovery', color: '#64748b', bg: '#f1f5f9', dot: '#94a3b8' },
  evaluation: { label: 'Evaluation', color: '#7c3aed', bg: '#f5f3ff', dot: '#a78bfa' },
  negotiation: { label: 'Negotiation', color: '#0891b2', bg: '#ecfeff', dot: '#22d3ee' },
  closing: { label: 'Closing', color: '#059669', bg: '#ecfdf5', dot: '#34d399' },
};

export const ynMeta: Record<
  YnValue,
  { label: string; bg: string; fg: string; dot: string }
> = {
  yes: { label: 'Yes', bg: '#ecfdf5', fg: '#047857', dot: '#10b981' },
  partial: { label: 'Partial', bg: '#fefce8', fg: '#a16207', dot: '#eab308' },
  no: { label: 'No', bg: '#fef2f2', fg: '#b91c1c', dot: '#ef4444' },
};

export const championStrengthMeta: Record<
  ChampionStrength,
  { label: string; bg: string; fg: string; dot: string }
> = {
  strong: { label: 'Strong', fg: '#047857', bg: '#ecfdf5', dot: '#10b981' },
  medium: { label: 'Medium', fg: '#a16207', bg: '#fefce8', dot: '#eab308' },
  weak: { label: 'Weak', fg: '#b91c1c', bg: '#fef2f2', dot: '#ef4444' },
};

export const msaMeta: Record<
  PipelineAccount['msaTerms'],
  { label: string; fg: string; bg: string }
> = {
  'not-sent': { label: 'Not sent', fg: '#64748b', bg: '#f1f5f9' },
  'in-review': { label: 'In review', fg: '#a16207', bg: '#fefce8' },
  redlined: { label: 'Redlined', fg: '#7c3aed', bg: '#f5f3ff' },
  signed: { label: 'Signed', fg: '#047857', bg: '#ecfdf5' },
};

export const vendorMeta: Record<
  PipelineAccount['vendorReg'],
  { label: string; fg: string; bg: string }
> = {
  'not-started': { label: 'Not started', fg: '#64748b', bg: '#f1f5f9' },
  'in-progress': { label: 'In progress', fg: '#a16207', bg: '#fefce8' },
  complete: { label: 'Complete', fg: '#047857', bg: '#ecfdf5' },
};

const LOGO_PALETTE = [
  '#1e3a8a', '#5b21b6', '#0891b2', '#7c3aed', '#0078d4', '#cc0033',
  '#86bc25', '#2e2e38', '#ff6b35', '#1e40af', '#7ab55c', '#635bff',
  '#0052cc', '#00a1e0', '#dc2626', '#16a34a', '#9333ea', '#ea580c',
];

const OWNER_PALETTE = ['#7c3aed', '#0891b2', '#dc2626', '#16a34a', '#0ea5e9', '#a16207'];

// ---------------------------------------------------------------------------
// Health computation — identical to data.js so the existing color thresholds
// match the design.
// ---------------------------------------------------------------------------
export function computeHealth(a: PipelineAccount): number {
  let s = 0;
  s += (a.momentum || 0) * 8;
  s += (a.strength || 0) * 6;
  if (a.ebSignedOff === 'yes') s += 10;
  else if (a.ebSignedOff === 'partial') s += 5;
  if (a.budgetApproved.value === 'yes') s += 10;
  else if (a.budgetApproved.value === 'partial') s += 5;
  s += Math.max(0, 10 - (a.blockers?.length || 0) * 2);
  return Math.min(100, Math.max(0, s));
}

export function healthBand(score: number): {
  band: 'healthy' | 'watch' | 'at-risk';
  color: string;
  bg: string;
  label: string;
} {
  if (score >= 75) return { band: 'healthy', color: '#059669', bg: '#ecfdf5', label: 'Healthy' };
  if (score >= 50) return { band: 'watch', color: '#a16207', bg: '#fefce8', label: 'Watch' };
  return { band: 'at-risk', color: '#b91c1c', bg: '#fef2f2', label: 'At risk' };
}

export function daysSince(date: Date | null | undefined): number {
  if (!date) return Infinity;
  return Math.floor((Date.now() - date.getTime()) / (1000 * 60 * 60 * 24));
}

export function fmtRelative(date: Date | null | undefined): string {
  const d = daysSince(date);
  if (d === Infinity) return '—';
  if (d === 0) return 'today';
  if (d === 1) return 'yesterday';
  if (d < 7) return `${d}d ago`;
  if (d < 30) return `${Math.floor(d / 7)}w ago`;
  return `${Math.floor(d / 30)}mo ago`;
}

export function fmtCurrency(n: number | null | undefined): string {
  if (!n) return '—';
  if (n >= 1_000_000) return `$${(n / 1_000_000).toFixed(1)}M`;
  if (n >= 1_000) return `$${Math.round(n / 1_000)}k`;
  return `$${n}`;
}

export function isStalled(a: PipelineAccount): boolean {
  return daysSince(a.lastActivity) > 21;
}

export type MissingField =
  | 'EB'
  | 'Champion'
  | 'Launch date'
  | 'Budget'
  | 'Legal'
  | 'Procurement';

export function missingFields(a: PipelineAccount): MissingField[] {
  const m: MissingField[] = [];
  if (!a.economicBuyer.value || a.economicBuyer.confidence === 'unknown') m.push('EB');
  if (!a.launchDateAgreed.value) m.push('Launch date');
  if (a.budgetApproved.value === 'no') m.push('Budget');
  if (!a.champion.name) m.push('Champion');
  if (!a.legalContact.name) m.push('Legal');
  if (!a.procurementContact.name) m.push('Procurement');
  return m;
}

// ---------------------------------------------------------------------------
// Derivations from existing AccountDoc + Salesforce opps snapshot
// ---------------------------------------------------------------------------

const STAGE_PRIORITY: Record<string, number> = {
  'Negotiation/Commit': 6,
  Negotiation: 5,
  Proposal: 4,
  Evaluation: 3,
  Discovery: 2,
  'Pre-qual': 1,
};

function mapSfStage(stageName: string | null | undefined): StageKey {
  switch (stageName) {
    case 'Negotiation/Commit':
      return 'closing';
    case 'Negotiation':
      return 'negotiation';
    case 'Proposal':
    case 'Evaluation':
      return 'evaluation';
    case 'Pre-qual':
    case 'Discovery':
    default:
      return 'discovery';
  }
}

interface SfOpportunityItem {
  Id?: string;
  Name?: string;
  StageName?: string;
  Amount?: number | null;
  CloseDate?: string | null;
  OwnerId?: string;
  Probability?: number | null;
  [k: string]: unknown;
}

export interface SfOpportunitiesSnapshot {
  items?: SfOpportunityItem[];
  count?: number;
}

function pickPrimaryOpp(items: SfOpportunityItem[] | undefined): SfOpportunityItem | null {
  if (!items?.length) return null;
  return [...items].sort((a, b) => {
    const aP = STAGE_PRIORITY[a.StageName ?? ''] ?? 0;
    const bP = STAGE_PRIORITY[b.StageName ?? ''] ?? 0;
    if (aP !== bP) return bP - aP;
    return (b.Amount ?? 0) - (a.Amount ?? 0);
  })[0];
}

function maxArr(items: SfOpportunityItem[] | undefined): number {
  if (!items?.length) return 0;
  return Math.max(0, ...items.map((i) => i.Amount ?? 0));
}

function logoLetters(name: string): string {
  const cleaned = name.replace(/[^A-Za-z0-9 ]/g, ' ').trim();
  const words = cleaned.split(/\s+/).filter(Boolean);
  if (words.length >= 2) return (words[0][0] + words[1][0]).toUpperCase();
  return cleaned.slice(0, 2).toUpperCase() || '··';
}

function hashIndex(s: string, mod: number): number {
  let h = 0;
  for (let i = 0; i < s.length; i++) {
    h = (h * 31 + s.charCodeAt(i)) >>> 0;
  }
  return h % mod;
}

function logoColorFor(id: string): string {
  return LOGO_PALETTE[hashIndex(id, LOGO_PALETTE.length)];
}

function ownerColorFor(seed: string): string {
  return OWNER_PALETTE[hashIndex(seed, OWNER_PALETTE.length)];
}

function initialsFromName(name: string): string {
  const parts = name.split(/\s+/).filter(Boolean);
  if (parts.length >= 2) return (parts[0][0] + parts[1][0]).toUpperCase();
  return name.slice(0, 2).toUpperCase() || '··';
}

// ---------------------------------------------------------------------------
// Heuristic parsers — the analyzer emits free-form strings; we coerce to the
// enums the design expects. "Unknown" / empty -> safest negative value.
// ---------------------------------------------------------------------------

function parseYn(value: string | undefined | null): YnValue {
  if (!value) return 'no';
  const v = value.trim().toLowerCase();
  if (v.startsWith('yes')) return 'yes';
  if (v.startsWith('partial') || v.includes('partial')) return 'partial';
  if (v.startsWith('no')) return 'no';
  return 'no';
}

function parseEb(value: string | undefined | null): PipelineEconomicBuyer {
  const v = (value ?? '').trim();
  if (!v || /^unknown$/i.test(v)) {
    return { value: null, confidence: 'unknown' };
  }
  const lower = v.toLowerCase();
  let confidence: Confidence = 'confirmed';
  if (
    lower.includes('unknown') ||
    lower.includes('unconfirmed') ||
    lower.includes('not identified') ||
    lower.includes('need name') ||
    lower.includes('tbd')
  ) {
    confidence = 'unknown';
  } else if (lower.includes('likely') || lower.includes('tentative') || lower.includes('possible')) {
    confidence = 'tentative';
  }
  return { value: v, confidence };
}

function parseLaunchDate(value: string | undefined | null): PipelineLaunchDate {
  const v = (value ?? '').trim();
  if (!v || /^(unknown|none|n\/a|tbd)$/i.test(v)) {
    return { value: null, status: 'no' };
  }
  // Try ISO-like or "MMM D, YYYY" / "YYYY-MM-DD"
  const isoMatch = v.match(/\d{4}-\d{2}-\d{2}/);
  let iso: string | null = null;
  if (isoMatch) {
    iso = isoMatch[0];
  } else {
    const parsed = Date.parse(v);
    if (!Number.isNaN(parsed)) {
      iso = new Date(parsed).toISOString().slice(0, 10);
    }
  }
  const lower = v.toLowerCase();
  let status: PipelineLaunchDate['status'] = iso ? 'tentative' : 'no';
  if (lower.includes('agreed') || lower.includes('confirmed') || lower.includes('locked')) {
    status = 'agreed';
  } else if (lower.includes('tentative') || lower.includes('target')) {
    status = 'tentative';
  } else if (!iso) {
    status = 'no';
  }
  return { value: iso, status };
}

function parseBudget(value: string | undefined | null): PipelineBudget {
  const v = (value ?? '').trim();
  return {
    value: parseYn(v),
    note: v && !/^(yes|no|partial|unknown)$/i.test(v) ? v : null,
  };
}

function parseChampion(
  value: string | undefined | null,
  ebSignedOff: YnValue,
  momentum: number,
): PipelineChampion {
  const v = (value ?? '').trim();
  if (!v || /^(unknown|none|missing)$/i.test(v)) {
    return { name: null, role: null, strength: 'weak' };
  }
  const [namePart, ...rest] = v.split(',');
  const role = rest.join(',').trim() || null;
  let strength: ChampionStrength = 'medium';
  if (ebSignedOff === 'yes' && momentum >= 4) strength = 'strong';
  else if (momentum <= 2) strength = 'weak';
  return { name: namePart.trim(), role, strength };
}

function parseContactName(value: string | undefined | null): { name: string | null } {
  const v = (value ?? '').trim();
  if (!v || /^(unknown|none|missing|tbd|n\/a)$/i.test(v)) return { name: null };
  return { name: v };
}

function parseMsa(value: string | undefined | null): PipelineAccount['msaTerms'] {
  const v = (value ?? '').trim().toLowerCase();
  if (!v) return 'not-sent';
  if (v.includes('signed')) return 'signed';
  if (v.includes('redline')) return 'redlined';
  if (v.includes('review') || v.includes('progress') || v.includes('sent')) return 'in-review';
  return 'not-sent';
}

function parseVendorReg(value: string | undefined | null): PipelineAccount['vendorReg'] {
  const v = (value ?? '').trim().toLowerCase();
  if (!v) return 'not-started';
  if (v.includes('complete') || v.startsWith('yes') || v.includes('registered')) return 'complete';
  if (v.includes('progress') || v.includes('partial') || v.includes('pending')) return 'in-progress';
  return 'not-started';
}

// ---------------------------------------------------------------------------
// Main adapter
// ---------------------------------------------------------------------------

interface CallerIdentity {
  uid: string;
  name: string;
  email: string;
}

export interface ToPipelineAccountInput {
  id: string;
  account: AccountDoc;
  opps: SfOpportunitiesSnapshot | null;
  caller?: CallerIdentity | null;
  /** SF User snapshot indexed by `Id`. Pass an empty Map if you haven't streamed `salesforceUsers/*` yet. */
  sfUsersById?: Map<string, SfUserSnapshot>;
}

function tsToDate(ts: Timestamp | null | undefined): Date | null {
  if (!ts) return null;
  try {
    return ts.toDate();
  } catch {
    return null;
  }
}

export function toPipelineAccount({
  id,
  account,
  opps,
  caller,
  sfUsersById,
}: ToPipelineAccountInput): PipelineAccount {
  const t = (account.dealTable ?? {}) as Partial<DealTable>;
  const primaryOpp = pickPrimaryOpp(opps?.items);
  const arr = maxArr(opps?.items);
  const stage = mapSfStage(primaryOpp?.StageName);
  const momentum = account.momentum ?? 0;
  const strength = account.strength ?? 0;

  // Owner: derived from `Opportunity.OwnerId` on the primary cohort opp,
  // joined against the `salesforceUsers/*` snapshot. SF owners are not
  // Firebase users, so `uid` is null; "My deals" matches by email.
  const ownerSfId =
    typeof primaryOpp?.OwnerId === 'string' && primaryOpp.OwnerId.length > 0
      ? primaryOpp.OwnerId
      : null;
  const sfOwner = ownerSfId ? (sfUsersById?.get(ownerSfId) ?? null) : null;
  const callerUid = caller?.uid ?? null;
  const callerEmail = caller?.email?.toLowerCase() ?? null;
  const sfOwnerEmail = sfOwner?.email?.toLowerCase() ?? null;

  let owner: PipelineOwner;
  if (sfOwner?.name) {
    owner = {
      name: sfOwner.name,
      initials: initialsFromName(sfOwner.name),
      color: ownerColorFor(sfOwnerEmail || ownerSfId || id),
      uid: callerEmail && sfOwnerEmail === callerEmail ? callerUid : null,
      sfUserId: ownerSfId,
      email: sfOwner.email ?? null,
    };
  } else {
    owner = {
      name: 'Unassigned',
      initials: '··',
      color: '#94a3b8',
      uid: null,
      sfUserId: ownerSfId,
      email: null,
    };
  }

  const eb = parseEb(t.economicBuyer);
  const ebSignedOff = parseYn(t.economicBuyerSignedOff);

  return {
    id,
    name: account.name || id,
    logo: logoLetters(account.name || id),
    logoColor: logoColorFor(id),
    stage,
    momentum,
    strength,
    arr,
    owner,
    economicBuyer: eb,
    ebSignedOff,
    useCase: t.useCase && t.useCase !== 'Unknown' ? t.useCase : null,
    launchDateAgreed: parseLaunchDate(t.tentativeLaunchDateAgreed),
    budgetApproved: parseBudget(t.budgetApprovedByEB),
    blockers: t.potentialBlockers ?? [],
    champion: parseChampion(t.champion, ebSignedOff, momentum),
    legalContact: parseContactName(t.legalContact),
    msaTerms: parseMsa(t.msaStatus),
    procurementContact: parseContactName(t.procurementContact),
    vendorReg: parseVendorReg(t.vendorRegistration),
    nextSteps: t.agreedNextSteps ?? [],
    redFlags: account.redFlags ?? [],
    calls: account.callCount ?? 0,
    emails: account.emailCount ?? 0,
    tableSources: account.dealTableSources,
    lastActivity: tsToDate(account.lastCallAt),
    updatedAt: tsToDate(account.updatedAt),
    hasAnalysis: !!account.dealTable,
  };
}
